import { useState, useCallback } from 'react'
import { supabase } from '@/services/supabase'
import type { User, Session, AuthError } from '@supabase/supabase-js'
import { stashOAuthSignupEntry } from '@/lib/signupEntry'

/**
 * useAuth Hook
 * Provides authentication operations for the application.
 *
 * Based on FLOW-01-AUTHENTICATION.md
 *
 * Features:
 * - Email/password signup and signin
 * - Google OAuth
 * - Password reset flow
 * - Email verification
 * - Session management
 */

export interface AuthState {
  user: User | null
  session: Session | null
  isLoading: boolean
  error: AuthError | null
}

export interface SignUpCredentials {
  email: string
  password: string
  fullName?: string
  /** Sign-up checkbox: the current Terms of Service version is recorded as accepted */
  acceptTerms?: boolean
  /** Sign-up entry of the register link (?from=), recorded with the account */
  signupEntry?: string | null
}

/**
 * OAuth can't carry sign-up metadata, so the register form's terms checkbox
 * survives the Google redirect in sessionStorage; the callback page records it.
 */
export const OAUTH_TERMS_ACCEPTED_KEY = 'lexalert.oauthTermsAccepted'

export interface SignInCredentials {
  email: string
  password: string
}

export interface UseAuthReturn extends AuthState {
  /** Sign up with email and password */
  signUp: (credentials: SignUpCredentials) => Promise<{ user: User | null; error: AuthError | null }>
  /** Sign in with email and password */
  signIn: (credentials: SignInCredentials) => Promise<{ user: User | null; error: AuthError | null }>
  /** Sign in with Google OAuth; acceptTerms = the register form's checkbox, signupEntry = its ?from= */
  signInWithGoogle: (options?: { acceptTerms?: boolean; signupEntry?: string | null }) => Promise<{ error: AuthError | null }>
  /** Sign out the current user */
  signOut: () => Promise<{ error: AuthError | null }>
  /** Send password reset email */
  resetPassword: (email: string) => Promise<{ error: AuthError | null }>
  /** Update password (after reset) */
  updatePassword: (newPassword: string) => Promise<{ error: AuthError | null }>
  /** Resend verification email */
  resendVerification: (email: string) => Promise<{ error: AuthError | null }>
  /** Clear any auth errors */
  clearError: () => void
}

export function useAuth(): UseAuthReturn {
  const [isLoading, setIsLoading] = useState(false)
  const [error, setError] = useState<AuthError | null>(null)
  const [user, setUser] = useState<User | null>(null)
  const [session, setSession] = useState<Session | null>(null)

  const clearError = useCallback(() => {
    setError(null)
  }, [])

  /**
   * Sign up with email and password
   * Creates a new user account and sends verification email
   */
  const signUp = useCallback(async (credentials: SignUpCredentials) => {
    setIsLoading(true)
    setError(null)

    try {
      const { data, error: signUpError } = await supabase.auth.signUp({
        email: credentials.email,
        password: credentials.password,
        options: {
          data: {
            full_name: credentials.fullName,
            terms_accepted: credentials.acceptTerms === true,
            ...(credentials.signupEntry ? { signup_entry: credentials.signupEntry } : {}),
          },
        },
      })

      if (signUpError) {
        setError(signUpError)
        return { user: null, error: signUpError }
      }

      setUser(data.user)
      setSession(data.session)
      return { user: data.user, error: null }
    } finally {
      setIsLoading(false)
    }
  }, [])

  /**
   * Sign in with email and password
   */
  const signIn = useCallback(async (credentials: SignInCredentials) => {
    setIsLoading(true)
    setError(null)

    try {
      const { data, error: signInError } = await supabase.auth.signInWithPassword({
        email: credentials.email,
        password: credentials.password,
      })

      if (signInError) {
        setError(signInError)
        return { user: null, error: signInError }
      }

      setUser(data.user)
      setSession(data.session)
      return { user: data.user, error: null }
    } finally {
      setIsLoading(false)
    }
  }, [])

  /**
   * Sign in with Google OAuth
   * Redirects to Google for authentication
   */
  const signInWithGoogle = useCallback(async (options?: { acceptTerms?: boolean; signupEntry?: string | null }) => {
    setIsLoading(true)
    setError(null)

    try {
      try {
        if (options?.acceptTerms) sessionStorage.setItem(OAUTH_TERMS_ACCEPTED_KEY, '1')
        else sessionStorage.removeItem(OAUTH_TERMS_ACCEPTED_KEY)
      } catch {
        // Storage unavailable: the in-app terms banner asks after sign-in
      }
      stashOAuthSignupEntry(options?.signupEntry)

      const { error: oauthError } = await supabase.auth.signInWithOAuth({
        provider: 'google',
        options: {
          redirectTo: `${window.location.origin}/auth/callback`,
        },
      })

      if (oauthError) {
        setError(oauthError)
        return { error: oauthError }
      }

      return { error: null }
    } finally {
      setIsLoading(false)
    }
  }, [])

  /**
   * Sign out the current user
   * Uses scope: 'local' to avoid network hang waiting for server revocation.
   * The server session will expire naturally (access tokens are short-lived).
   */
  const signOut = useCallback(async () => {
    setIsLoading(true)
    setError(null)

    try {
      // Use scope: 'local' to only clear local session storage.
      // This avoids hanging on network requests or Navigator Lock contention.
      // The server-side session will expire on its own.
      const { error: signOutError } = await supabase.auth.signOut({ scope: 'local' })

      if (signOutError) {
        setError(signOutError)
        return { error: signOutError }
      }

      setUser(null)
      setSession(null)
      return { error: null }
    } finally {
      setIsLoading(false)
    }
  }, [])

  /**
   * Send password reset email
   */
  const resetPassword = useCallback(async (email: string) => {
    setIsLoading(true)
    setError(null)

    try {
      const { error: resetError } = await supabase.auth.resetPasswordForEmail(email, {
        redirectTo: `${window.location.origin}/reset-password`,
      })

      if (resetError) {
        setError(resetError)
        return { error: resetError }
      }

      return { error: null }
    } finally {
      setIsLoading(false)
    }
  }, [])

  /**
   * Update password (after reset link clicked)
   */
  const updatePassword = useCallback(async (newPassword: string) => {
    setIsLoading(true)
    setError(null)

    try {
      const { error: updateError } = await supabase.auth.updateUser({
        password: newPassword,
      })

      if (updateError) {
        setError(updateError)
        return { error: updateError }
      }

      return { error: null }
    } finally {
      setIsLoading(false)
    }
  }, [])

  /**
   * Resend verification email
   */
  const resendVerification = useCallback(async (email: string) => {
    setIsLoading(true)
    setError(null)

    try {
      const { error: resendError } = await supabase.auth.resend({
        type: 'signup',
        email,
      })

      if (resendError) {
        setError(resendError)
        return { error: resendError }
      }

      return { error: null }
    } finally {
      setIsLoading(false)
    }
  }, [])

  return {
    user,
    session,
    isLoading,
    error,
    signUp,
    signIn,
    signInWithGoogle,
    signOut,
    resetPassword,
    updatePassword,
    resendVerification,
    clearError,
  }
}
